import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Input, InputNumber, message, Modal as AntdModal, Radio, Select, Space, Table, Tag } from 'antd';
import Modal from '../../../../components/Modal';
import {
  BatchDetailItem,
  RollbackScope,
  settlementDateAdjustmentApi,
  VerifyResponse,
} from '../../../../lib/api/settlementDateAdjustment';
import { settlementDateAdjustmentQuery } from '../../queries';
import {
  ROLLBACK_SCOPE_LABEL,
  SETTLEMENT_DATE_ADJUSTMENT_BATCH_STATUS_LABEL,
  SETTLEMENT_DATE_ADJUSTMENT_SOURCE_LABEL,
} from '../../constants/labels';
import { formatDate } from '../../../../lib/utils/date/formatDate';
import { DATE_FORMAT } from '../../../../types/common/date';
import { useRollbackMutation } from '../../hooks/useAdjustmentMutations';
import {
  useBatchScopeOptions,
  validateRollbackInputs,
} from '../../../settlement/hooks/useBatchRollbackInputs';

type Props = {
  batchId: string | null;
  onClose: () => void;
};

/**
 * 배치 상세 + 롤백 + 검증(verify) 모달.
 */
export const BatchDetailModal = ({ batchId, onClose }: Props): React.JSX.Element => {
  const [rollbackReason, setRollbackReason] = useState('');
  const [rollbackScope, setRollbackScope] = useState<RollbackScope>('all');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // byStoreId / byOrderItemIds 입력값. scope 전환 시 초기화해 값 혼선 방지.
  const [rollbackStoreId, setRollbackStoreId] = useState<number | null>(null);
  const [rollbackOrderItemIds, setRollbackOrderItemIds] = useState<string[]>([]);
  const [verifyResult, setVerifyResult] = useState<VerifyResponse | null>(null);

  const detailQuery = useQuery({
    ...settlementDateAdjustmentQuery.batchDetail(batchId ?? ''),
    enabled: !!batchId,
  });
  const rollbackMutation = useRollbackMutation(batchId ?? '');

  // scope 드롭다운에 제안할 값들 — 공유 훅으로 derive (완료일 변경 도메인과 동일 로직).
  const { storeOptionList: batchStoreOptionList, orderItemIds: batchOrderItemIds } =
    useBatchScopeOptions(detailQuery.data?.items);

  const handleRollback = () => {
    // scope 별 필요 필드가 갖춰졌는지 사전 검증 — 공유 utility 사용.
    const error = validateRollbackInputs({
      scope: rollbackScope,
      reason: rollbackReason,
      selectedIds,
      storeId: rollbackStoreId,
      orderItemIds: rollbackOrderItemIds,
    });
    if (error) {
      message.warning(error);
      return;
    }
    AntdModal.confirm({
      title: '롤백을 실행합니다',
      content: `롤백 범위: ${ROLLBACK_SCOPE_LABEL[rollbackScope]}. 이 작업도 변경 이력에 기록됩니다.`,
      okText: '롤백',
      okButtonProps: { danger: true },
      cancelText: '취소',
      onOk: async () => {
        try {
          const res = await rollbackMutation.mutateAsync({
            scope: rollbackScope,
            ids: rollbackScope === 'byIds' ? selectedIds : undefined,
            storeId:
              rollbackScope === 'byStoreId' ? rollbackStoreId ?? undefined : undefined,
            orderItemIds:
              rollbackScope === 'byOrderItemIds' ? rollbackOrderItemIds : undefined,
            reason: rollbackReason,
          });
          const statusLabel =
            SETTLEMENT_DATE_ADJUSTMENT_BATCH_STATUS_LABEL[res.batchStatus] ??
            res.batchStatus;
          message.success(
            `${res.rolledBackCount}건 롤백 완료. 남은 활성 ${res.remainingCount}건. 배치 상태: ${statusLabel}`,
          );
          setRollbackReason('');
          setSelectedIds([]);
          setRollbackStoreId(null);
          setRollbackOrderItemIds([]);
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : String(e);
          message.error(`롤백 실패: ${msg}`);
        }
      },
    });
  };

  const handleVerify = async (): Promise<void> => {
    if (!batchId) return;
    try {
      const res = await settlementDateAdjustmentApi.verify(batchId);
      setVerifyResult(res);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      message.error(`검증 실패: ${msg}`);
    }
  };

  const batch = detailQuery.data?.batch;
  const items = detailQuery.data?.items ?? [];

  const itemColumns = [
    { title: '정산 ID', dataIndex: 'settlementItemId', width: 90 },
    { title: '브랜드ID', dataIndex: 'storeId', width: 80 },
    {
      title: '브랜드명',
      dataIndex: 'storeName',
      width: 140,
      // store 가 삭제되었거나 응답 보강 누락 시 undefined → "-" 로 표시.
      render: (v?: string) => v ?? '-',
    },
    {
      title: '상품 주문번호',
      dataIndex: 'orderItemId',
      width: 180,
      render: (v?: string) => v ?? '-',
    },
    { title: '변경 전 정산 기준일', dataIndex: 'beforeBaseDate', width: 140 },
    { title: '변경 후 정산 기준일', dataIndex: 'afterBaseDate', width: 140 },
    { title: '변경 전 정산 예정일', dataIndex: 'beforeDueDate', width: 140 },
    {
      title: '변경 후 정산 예정일',
      key: 'afterDueDate',
      width: 180,
      render: (_: unknown, r: BatchDetailItem) => (
        <Space size={4}>
          <span>{r.afterDueDate}</span>
          {r.isManualDueDate && <Tag color="blue">수동</Tag>}
        </Space>
      ),
    },
    {
      title: '롤백 여부',
      key: 'state',
      width: 120,
      render: (_: unknown, r: BatchDetailItem) =>
        r.rolledBackAt ? <Tag color="default">롤백됨</Tag> : <Tag color="blue">변경 적용 중</Tag>,
    },
  ];

  return (
    <Modal
      isOpen={!!batchId}
      width={1200}
      title="배치 상세"
      onClose={onClose}
      padding="20px"
      destroyOnClose
    >
      {detailQuery.isLoading && <div>로딩 중...</div>}
      {batch && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Descriptions size="small" bordered column={3}>
            <Descriptions.Item label="배치 ID" span={3}>
              {batch.batchId}
            </Descriptions.Item>
            <Descriptions.Item label="실행 관리자">{batch.adminName}</Descriptions.Item>
            <Descriptions.Item label="실행 일시">
              {formatDate(batch.executedAt, DATE_FORMAT.FULL_DASH_FORMAT)}
            </Descriptions.Item>
            <Descriptions.Item label="배치 상태">
              {SETTLEMENT_DATE_ADJUSTMENT_BATCH_STATUS_LABEL[batch.status] ?? batch.status}
            </Descriptions.Item>
            <Descriptions.Item label="대상/적용/롤백 건수">
              {batch.totalCount} / {batch.appliedCount} / {batch.rolledBackCount}
            </Descriptions.Item>
            <Descriptions.Item label="이동 내용">
              {batch.shiftMode === 'delta' ? `+${batch.deltaDays ?? 0}일` : `→ ${batch.targetBaseDate}`}
            </Descriptions.Item>
            <Descriptions.Item label="선정 방식">
              {batch.source ? (
                <Tag color={batch.source === 'excelUpload' ? 'geekblue' : 'default'}>
                  {SETTLEMENT_DATE_ADJUSTMENT_SOURCE_LABEL[batch.source]}
                </Tag>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="변경 사유" span={3}>
              {batch.reason}
            </Descriptions.Item>
          </Descriptions>

          <Card size="small" title="변경된 정산아이템 리스트">
            <Table<BatchDetailItem>
              size="small"
              rowKey="settlementItemId"
              dataSource={items}
              columns={itemColumns}
              rowSelection={{
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelectedIds(keys as number[]),
                getCheckboxProps: (r) => ({ disabled: !!r.rolledBackAt }),
              }}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
            />
          </Card>

          <Card size="small" title="롤백">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Radio.Group
                value={rollbackScope}
                onChange={(e) => {
                  // scope 전환 시 다른 입력 초기화 — 값이 남아 혼선되지 않도록.
                  setRollbackScope(e.target.value);
                  setSelectedIds([]);
                  setRollbackStoreId(null);
                  setRollbackOrderItemIds([]);
                }}
              >
                <Space direction="vertical" size={4}>
                  <Radio value="all">{ROLLBACK_SCOPE_LABEL.all} (미롤백 항목 전부)</Radio>
                  <Radio value="byIds">{ROLLBACK_SCOPE_LABEL.byIds} (위 체크박스 선택)</Radio>
                  <Radio value="byStoreId">
                    {ROLLBACK_SCOPE_LABEL.byStoreId} (특정 브랜드만 원복)
                  </Radio>
                  <Radio value="byOrderItemIds">
                    {ROLLBACK_SCOPE_LABEL.byOrderItemIds} (특정 상품 주문만 원복)
                  </Radio>
                </Space>
              </Radio.Group>

              {/* scope별 추가 입력 */}
              {rollbackScope === 'byStoreId' && (
                <Space>
                  <span style={{ fontSize: 13 }}>대상 브랜드 ID:</span>
                  <Select<number>
                    style={{ minWidth: 220 }}
                    placeholder="배치 내 브랜드 선택"
                    value={rollbackStoreId ?? undefined}
                    onChange={(v) => setRollbackStoreId(v)}
                    options={batchStoreOptionList.map(({ storeId, storeName }) => ({
                      // storeName 없으면(보강 실패/store 삭제) id 만 표기.
                      label: storeName ? `${storeId} — ${storeName}` : String(storeId),
                      value: storeId,
                    }))}
                    showSearch
                    optionFilterProp="label"
                  />
                  {/* 배치 외 브랜드 ID 를 직접 입력해야 하는 예외 케이스 대비 */}
                  <InputNumber
                    min={1}
                    placeholder="직접 입력"
                    value={rollbackStoreId ?? undefined}
                    onChange={(v) =>
                      setRollbackStoreId(typeof v === 'number' ? v : null)
                    }
                  />
                </Space>
              )}

              {rollbackScope === 'byOrderItemIds' && (
                <>
                  <span style={{ fontSize: 13 }}>대상 상품 주문번호:</span>
                  <Select<string[]>
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder="배치 내 상품 주문번호 선택 또는 직접 입력"
                    value={rollbackOrderItemIds}
                    onChange={(v) => setRollbackOrderItemIds(v)}
                    options={batchOrderItemIds.map((id) => ({
                      label: id,
                      value: id,
                    }))}
                    tokenSeparators={[',', ' ']}
                  />
                </>
              )}

              <Input.TextArea
                rows={2}
                placeholder="롤백 사유 (필수)"
                value={rollbackReason}
                onChange={(e) => setRollbackReason(e.target.value)}
              />
              <Button danger onClick={handleRollback} loading={rollbackMutation.isLoading}>
                롤백 실행
              </Button>
            </Space>
          </Card>

          <Card size="small" title="검증 (verify)">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Button onClick={handleVerify}>현재 상태와 비교</Button>
              {verifyResult && (
                <>
                  <Alert
                    type={
                      verifyResult.mismatchedCount === 0 && verifyResult.createdAfterBatch.length === 0
                        ? 'success'
                        : 'warning'
                    }
                    showIcon
                    message={`일치 ${verifyResult.matchedCount}건 / 불일치 ${verifyResult.mismatchedCount}건 / 이후 생성 ${verifyResult.createdAfterBatch.length}건`}
                  />
                  {verifyResult.mismatches.length > 0 && (
                    <div>
                      <b>불일치 항목:</b>
                      <pre style={{ fontSize: 12, maxHeight: 200, overflow: 'auto' }}>
                        {JSON.stringify(verifyResult.mismatches, null, 2)}
                      </pre>
                    </div>
                  )}
                  {verifyResult.createdAfterBatch.length > 0 && (
                    <div>
                      <b>배치 이후 생성된 extraDelivery/returnDelivery:</b>
                      <pre style={{ fontSize: 12, maxHeight: 200, overflow: 'auto' }}>
                        {JSON.stringify(verifyResult.createdAfterBatch, null, 2)}
                      </pre>
                    </div>
                  )}
                </>
              )}
            </Space>
          </Card>
        </Space>
      )}
    </Modal>
  );
};
